import { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import { obtenerCartaDocxBlob } from "@/lib/api";
import { useDescargaCarta } from "@/hooks/useDescargaCarta";
import { formatearFechaCorta } from "@/lib/utils";
import type { ExpedienteAnalizado } from "@/types";

// IDs de plantillas que SÍ tienen archivo `.docx` físico en el
// catálogo (gema/data/plantillas/afinia/). Si el plantilla_id del
// expediente no está acá, el backend cae al fallback
// `construir_docx_desde_proyeccion` y la UI debe avisar al operador
// que está usando una proyección genérica (fix B).
const PLANTILLAS_CATALOGO_FISICO = new Set<string>([
  "AFINIA_TIPO_1_COBRO_RECONEXION",
  "AFINIA_TIPO_2_ERROR_LECTURA",
  // A: rutas administrativas con plantilla física.
  "AFINIA_TIPO_3_ACCEDE_SILENCIO_158",
  "AFINIA_TIPO_3_NO_ACCEDE_ADMIN",
  "AFINIA_TIPO_3_IMPROCEDENTE_ART87",
  "AFINIA_TIPO_4_INCONFORMIDAD_CONSUMO",
  "AFINIA_TIPO_5A_REITERATIVO_EN_CURSO",
  "AFINIA_TIPO_5B_REITERATIVO_RESUELTO",
]);

export function esPlantillaFallback(id: string | undefined): boolean {
  if (!id) return true;
  return !PLANTILLAS_CATALOGO_FISICO.has(id);
}

// Mapa de plantilla_id → nombre amigable para el operador.
export function nombrePlantilla(id: string | undefined): string {
  if (!id) return "Plantilla genérica";

  // 1) Mapa explícito para los IDs más comunes.
  const mapa: Record<string, string> = {
    AFC_PLANT_ACCEDE: "Plantilla ACCEDE",
    AFC_PLANT_ACCEDE_PARCIAL: "Plantilla ACCEDE parcial",
    AFC_PLANT_NO_ACCEDE: "Plantilla NO ACCEDE",
    AFC_PLANT_NO_ACCEDE_ADMIN: "NO ACCEDE administrativa (Art. 128/130)",
    AFC_PLANT_IMPROCEDENTE: "Plantilla IMPROCEDENTE",
    AFC_PLANT_SAP_158: "Silencio Administrativo Positivo (Art. 158)",
    AFINIA_TIPO_1_RECONEXION: "AFINIA TIPO 1 · Cobro de reconexión",
    AFINIA_TIPO_2_ERROR_LECTURA: "AFINIA TIPO 2 · Error de lectura",
    AFINIA_TIPO_3_ACCEDE_SILENCIO_158:
      "AFINIA TIPO 3 · Silencio Administrativo Positivo (Art. 158)",
    AFINIA_TIPO_3_NO_ACCEDE_ADMIN:
      "AFINIA TIPO 3 · NO ACCEDE administrativa",
    AFINIA_TIPO_3_IMPROCEDENTE_ART87:
      "AFINIA TIPO 3 · IMPROCEDENTE reiteración (Art. 87 CPACA)",
    AFINIA_TIPO_4_INCONFORMIDAD_CONSUMO: "AFINIA TIPO 4 · Inconformidad por consumo",
    AFINIA_TIPO_5A_REITERATIVO_EN_CURSO: "AFINIA TIPO 5A · Reiterativo en curso",
    AFINIA_TIPO_5B_REITERATIVO_RESUELTO: "AFINIA TIPO 5B · Reiterativo con respuesta emitida",
    GEMA_PLANTILLA_GENERICA: "Plantilla genérica GEMA",
  };
  if (mapa[id]) return mapa[id];

  // 2) Parser por convención cuando el ID no está en el mapa pero
  // sigue un patrón conocido (ej. AFC_PLANT_NO_ACCEDE_ART_146).
  const m = id.match(/^AFC_PLANT_(NO_ACCEDE|ACCEDE|ACCEDE_PARCIAL|IMPROCEDENTE)_ART_(\d+)$/);
  if (m) {
    const dec = m[1].replace("_", " ");
    return `${dec} · Art. ${m[2]}`;
  }
  const m2 = id.match(/^AFC_PLANT_(NO_ACCEDE|ACCEDE|ACCEDE_PARCIAL|IMPROCEDENTE)$/);
  if (m2) return `Plantilla ${m2[1].replace("_", " ")}`;

  // 3) Fallback: limpiar el ID para mostrarlo legible.
  return id.replace(/^AFC_PLANT_/, "").replace(/_/g, " ");
}

export function CartaProyectadaCalm({ expediente }: { expediente: ExpedienteAnalizado }) {
  const proy = expediente.proyeccion_carta;
  const {
    descargar: onDescargar,
    descargando,
    error: errorDescarga,
  } = useDescargaCarta(
    expediente.expediente_id,
    `GEMA_${expediente.empresa}_${expediente.numero_radicado}.docx`,
  );
  if (!proy) return null;
  const obsoleta = expediente.proyeccion_carta_obsoleta;
  const empresa = expediente.empresa === "AFINIA"
    ? "CARIBEMAR DE LA COSTA S.A.S. E.S.P."
    : "AIR-E S.A.S. E.S.P.";
  const nit = expediente.empresa === "AFINIA" ? "901.404.486-1" : "901.412.901-1";
  const ciudad = expediente.empresa === "AFINIA" ? "Cartagena de Indias" : "Barranquilla";
  const hoyISO = formatearFechaCorta(new Date().toISOString());
  const plantillaId = expediente.decision?.metadata.plantilla_id;
  const plantillaNombre = nombrePlantilla(plantillaId);
  const placeholdersPendientes = expediente.placeholders_pendientes?.length ?? 0;
  const usaFallback = esPlantillaFallback(plantillaId);

  return (
    <section>
      {/* Header de la carta con plantilla + botón descarga verde grande.
          Si es fallback (sin plantilla del catálogo), el header se pinta
          en ámbar para que el operador sepa que está usando una
          proyección genérica del redactor LLM (fix B). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 14,
          padding: "14px 18px",
          background: usaFallback
            ? "var(--amber-soft, oklch(0.95 0.05 80))"
            : "var(--surface)",
          border: usaFallback
            ? "1px solid oklch(0.85 0.10 80)"
            : "1px solid var(--border)",
          borderRadius: "var(--r-lg)",
        }}
      >
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>
            {usaFallback
              ? "Carta proyectada · sin plantilla del catálogo"
              : "Carta proyectada"}
          </div>
          <div
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              marginBottom: 4,
            }}
          >
            {plantillaNombre}
          </div>
          <div
            className="row row-2"
            style={{ fontSize: 12, color: "var(--ink-3)", flexWrap: "wrap" }}
          >
            <span className="mono" title="ID técnico de la plantilla">
              {plantillaId ?? "—"}
            </span>
            <span>·</span>
            {usaFallback ? (
              <span
                style={{ color: "var(--amber-deep)", fontWeight: 500 }}
                title="No hay archivo .docx en el catálogo Afinia para esta ruta. La carta se genera desde la proyección del redactor LLM y el operador debe revisarla antes de notificar."
              >
                ⚠ Proyección genérica (revisar antes de notificar)
              </span>
            ) : (
              <span>
                {placeholdersPendientes === 0 ? (
                  <span style={{ color: "var(--emerald-deep)" }}>
                    ✓ Datos completos
                  </span>
                ) : (
                  <span style={{ color: "var(--amber-deep)" }}>
                    ⚠ {placeholdersPendientes} dato(s) pendiente(s)
                  </span>
                )}
              </span>
            )}
            {obsoleta && (
              <>
                <span>·</span>
                <span className="badge badge--amber">
                  <span className="dot" />
                  Obsoleta — re-procesar
                </span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onDescargar}
          disabled={descargando}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "0 22px",
            height: 44,
            background: "var(--emerald-deep)",
            color: "white",
            border: 0,
            borderRadius: "var(--r-md)",
            fontSize: 14,
            fontWeight: 600,
            cursor: descargando ? "wait" : "pointer",
            boxShadow: "0 1px 2px oklch(0.30 0.08 162 / 0.25), 0 4px 12px oklch(0.30 0.08 162 / 0.15)",
            opacity: descargando ? 0.7 : 1,
            transition: "all .14s",
          }}
          onMouseEnter={(e) => {
            if (!descargando) {
              e.currentTarget.style.background = "oklch(0.30 0.08 162)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--emerald-deep)";
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path
              d="M3 10v2.5A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5V10M8 2v8M5 7l3 3 3-3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {descargando ? "Generando .docx…" : "Descargar carta"}
        </button>
      </div>
      {errorDescarga && (
        <div className="banner banner--red" style={{ marginBottom: 14 }}>
          <span className="banner__icon">⚠</span>
          <div>{errorDescarga}</div>
        </div>
      )}

      {/* Preview FIEL del DOCX que se descarga (docx-preview) */}
      <PreviewCartaDocx
        expedienteId={expediente.expediente_id}
        plantillaId={plantillaId}
        fallback={
          <FallbackCartaTexto
            empresa={empresa}
            nit={nit}
            ciudad={ciudad}
            hoyISO={hoyISO}
            expediente={expediente}
            proy={proy}
          />
        }
      />
    </section>
  );
}

function CartaContenido({
  proy,
  expediente: _expediente,
}: {
  proy: import("@/types").ProyeccionCarta;
  expediente: ExpedienteAnalizado;
}) {
  // El backend devuelve la carta como bloque de texto (encabezado +
  // considerandos + parte resolutiva + recursos). Lo renderizamos
  // como párrafos serif separados.
  const secciones: Array<{ titulo?: string; cuerpo: string }> = [];
  if (proy.encabezado) secciones.push({ cuerpo: proy.encabezado });
  if (proy.considerandos && proy.considerandos.length > 0) {
    secciones.push({
      titulo: "I. Consideraciones",
      cuerpo: proy.considerandos.join("\n\n"),
    });
  }
  if (proy.parte_resolutiva) {
    secciones.push({
      titulo: "II. Parte resolutiva",
      cuerpo: proy.parte_resolutiva,
    });
  }
  if (proy.notificacion_recursos) {
    secciones.push({
      titulo: "III. Recursos",
      cuerpo: proy.notificacion_recursos,
    });
  }
  return (
    <>
      {secciones.map((s, i) => (
        <div key={i} style={{ marginTop: s.titulo ? 32 : 24 }}>
          {s.titulo && (
            <h3
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 17,
                fontWeight: 500,
                marginTop: 0,
                marginBottom: 10,
                letterSpacing: "-0.005em",
              }}
            >
              {s.titulo}
            </h3>
          )}
          <div style={{ margin: 0, whiteSpace: "pre-wrap" }}>{s.cuerpo}</div>
        </div>
      ))}
    </>
  );
}

function PreviewCartaDocx({
  expedienteId,
  plantillaId,
  fallback,
}: {
  expedienteId: string;
  plantillaId: string | undefined;
  fallback: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const el = containerRef.current;
    if (!el) return;

    (async () => {
      try {
        setCargando(true);
        setError(null);
        const blob = await obtenerCartaDocxBlob(expedienteId);
        if (cancelado) return;
        el.innerHTML = "";
        await renderAsync(blob, el, undefined, {
          // `inWrapper: false` evita el "fondo gris tipo Word" que
          // emula el cliente de escritorio. Renderizamos la hoja
          // directamente en el contenedor con nuestro estilo calm.
          inWrapper: false,
          ignoreWidth: true,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: false,
          experimental: true,
          className: "gema-docx",
        });
        if (!cancelado) setCargando(false);
      } catch (exc) {
        if (cancelado) return;
        setError((exc as Error).message ?? "Error al renderizar la carta");
        setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
    // Recargamos cuando cambie el expediente o su plantilla (cambio
    // de decisión re-renderiza con la nueva plantilla seleccionada).
  }, [expedienteId, plantillaId]);

  if (error) {
    return (
      <div>
        <div className="banner banner--red" style={{ marginBottom: 14 }}>
          <span className="banner__icon">⚠</span>
          <div>
            No se pudo renderizar el preview del DOCX ({error}). Mostrando la
            proyección en texto plano como respaldo:
          </div>
        </div>
        {fallback}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {cargando && (
        <div
          style={{
            padding: 28,
            textAlign: "center",
            color: "var(--ink-3)",
            fontSize: 13,
          }}
        >
          Renderizando la carta proyectada…
        </div>
      )}
      <div
        ref={containerRef}
        className="docx-preview-host"
        style={{
          display: cargando ? "none" : "block",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)",
          padding: "40px 48px",
          minHeight: 320,
          overflowX: "auto",
        }}
      />
    </div>
  );
}

function FallbackCartaTexto({
  empresa,
  nit,
  ciudad,
  hoyISO,
  expediente,
  proy,
}: {
  empresa: string;
  nit: string;
  ciudad: string;
  hoyISO: string;
  expediente: ExpedienteAnalizado;
  proy: import("@/types").ProyeccionCarta;
}) {
  return (
    <article className="carta-proyectada">
      <div className="carta-proyectada__tag">
        <span
          className="tag"
          style={{
            background: "var(--surface-sunken)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: "2px 7px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--ink-2)",
          }}
        >
          borrador · texto plano (fallback)
        </span>
      </div>
      <div className="carta-proyectada__body">
        <div className="carta-proyectada__membrete">
          {empresa}
          <br />
          NIT {nit} · {ciudad}, {hoyISO}
        </div>
        <CartaContenido proy={proy} expediente={expediente} />
        <div className="carta-proyectada__footer">
          Generado por GEMA Motor · pendiente firma supervisor · radicado{" "}
          <span className="mono">{expediente.numero_radicado}</span>
        </div>
      </div>
    </article>
  );
}

